import PropTypes from 'prop-types'
import { Fragment } from 'react'

import TimePeriodForInvocation from './TimePeriodForInvocation'

import Button from 'components/common/Button'
import DayPicker from 'components/common/DayPicker'
import Popover from 'components/common/Popover'
import TextButton from 'components/common/TextButton'
import Text from 'components/common/Text'
import Pill from 'components/common/Pill'
import Icon from 'components/Icon'
import PopoverMenu from 'components/common/PopoverMenu'
import DataTable from 'components/common/DataTable'
import Flex from 'components/common/Flex'
import MaxRuns from 'components/schedules/MaxRuns'
import { messageFromResponseError, pluralize } from 'utils/strings'
import useAuth from 'hooks/use-auth'
import useScenarioScheduleDetails from 'hooks/use-scenario-schedule-details'
import agent from 'agent'
import toast from 'lib/toast'
import theme from 'theme'
import KubernetesTargets from 'components/attacks/list/KubernetesTargets'
import { isKubernetes } from 'components/attacks/create/targets/target-utils'
import {
  EmptyState,
  EmptyStateText,
  EmptyStateTitle
} from 'components/common/EmptyState'
import { useGremlinQuery } from 'hooks/use-gremlin-query'

function ScheduleListItem({ schedule, currentTeamId, doFetch }) {
  const {
    name,
    stepCount,
    firstTargetParam,
    targetDefinition,
    additionalTargetParamsAmount
  } = useScenarioScheduleDetails(schedule.scenario_id)

  const isFirstAttackKubernetes = isKubernetes(targetDefinition?.targetType)

  return (
    <Fragment>
      <div>
        <Text as="h5">{name}</Text>
        <div>
          <MaxRuns
            amount={schedule.trigger.maxRuns}
            css={{ display: 'inline' }}
          />
          <Text as="small" css={{ display: 'inline' }}>
            , {stepCount} {pluralize('step', stepCount)}
          </Text>
        </div>
      </div>
      <TimePeriodForInvocation schedule={schedule} />
      <DayPicker isStatic activeDays={schedule.trigger.activeDays} />
      <Flex>
        {isFirstAttackKubernetes ? (
          <KubernetesTargets targets={targetDefinition?.strategy?.k8sObjects} />
        ) : null}
        {!isFirstAttackKubernetes && firstTargetParam ? (
          <Pill
            key="firstParam"
            tooltip={`${firstTargetParam[0]}: ${firstTargetParam[1]}`}
          >
            {firstTargetParam[1]}
          </Pill>
        ) : null}
        {!isFirstAttackKubernetes && additionalTargetParamsAmount ? (
          <Pill key="paramCount" type="gray">
            + {additionalTargetParamsAmount}
          </Pill>
        ) : null}
      </Flex>
      <div className="visible-on-row-hover">
        <Popover alignPopover={Popover.ALIGN_RIGHT}>
          {({ getTargetProps, getPopoverProps }) => (
            <Fragment>
              <Icon
                data-test-schedule-actions={schedule.guid}
                icon="ellipsisVertical"
                {...getTargetProps({
                  css: {
                    fontSize: theme.fontSizes[3],
                    color: theme.colors.steel[0],

                    '&:hover': {
                      color: theme.colors.steel[2]
                    }
                  }
                })}
              />
              <PopoverMenu getPopoverProps={getPopoverProps}>
                <TextButton
                  destructive
                  data-test-schedule-delete={schedule.guid}
                  onClick={async event => {
                    event.preventDefault()
                    try {
                      await agent.delete(
                        `/schedules/scenarios/${schedule.guid}?teamId=${currentTeamId}`
                      )
                      toast({
                        type: 'success',
                        text: 'Schedule deleted.'
                      })
                    } catch (err) {
                      toast({
                        type: 'error',
                        text: messageFromResponseError(
                          err,
                          'Unable to cancel schedule. Please try again later.'
                        )
                      })
                    }
                    doFetch()
                  }}
                >
                  Delete Scenario Schedule
                </TextButton>
              </PopoverMenu>
            </Fragment>
          )}
        </Popover>
      </div>
    </Fragment>
  )
}

ScheduleListItem.propTypes = {
  schedule: PropTypes.object,
  currentTeamId: PropTypes.string,
  doFetch: PropTypes.func
}

export default function ScenariosScheduleList() {
  const { currentTeamId } = useAuth()

  const { data, isError, isLoading, refetch } = useGremlinQuery(
    '/schedules/scenarios'
  )

  return (
    <DataTable
      columnTitles={['Name', 'Time', 'Days', 'Targets', false]}
      data={data}
      error={isError}
      getRowLinkTo={({ scenario_id }) => `/scenarios/detail/${scenario_id}`}
      gridColumns="minmax(48px, 1fr) minmax(48px, 128px) minmax(256px, 1fr) minmax(128px, 1fr) 32px"
      loading={isLoading}
      renderEmpty={() => (
        <EmptyState>
          <EmptyStateTitle>No schedules available</EmptyStateTitle>
          <EmptyStateText>Create an attack to schedule.</EmptyStateText>
        </EmptyState>
      )}
      renderError={message => (
        <EmptyState>
          <EmptyStateTitle>
            {message || 'There was an error loading schedules'}
          </EmptyStateTitle>
          <Button
            loading={isLoading}
            type="button"
            onClick={() => {
              refetch()
            }}
          >
            Retry
          </Button>
        </EmptyState>
      )}
      renderRow={schedule => (
        <ScheduleListItem
          currentTeamId={currentTeamId}
          doFetch={refetch}
          schedule={schedule}
        />
      )}
    />
  )
}
